/**
 * The dark page hero shared by the careers sub-pages and the metal-solutions pages: breadcrumb,
 * title, sub-title and description on the left, and a YouTube video (or a still image when there
 * is no `videoId`) on the right. Below `lg` it stacks the title and sub-title, then the media,
 * then the description and button.
 *
 * Position: 19 pages render it. With `isCareers` set it ends with the job-board buttons
 * (`components/Shared/CareersButtons/CareersButtons.tsx`): `pages/about-us/careers/index.tsx`,
 * the four sub-pages under `pages/about-us/careers/` (`grow-with-sales/`,
 * `innovate-with-operations/`, `opportunities-for-veterans/`, `strategize-with-finance-hr-it/`)
 * and the STS `pages/who-we-are/careers/index.tsx`. Without it, it shows one optional button from
 * `buttonUrl` / `buttonLabel`: the Ryerson `pages/metal-solutions/solutions/*` pages (for example
 * `risk-management/`) and the STS `solutions/fast-shipments/`, `solutions/non-stock-procurements/`
 * and `services/*` pages.
 *
 * {@link GrowHeroContent} is the copy shape for all of those pages' `src/content/` modules. The STS
 * industries-served page uses the layout variant `./GrowHeroSTS.tsx`, which repeats this type.
 */
import React from "react";
import { useTheme } from "@ryerson/frontend.theme";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { ContentSection } from "@ryerson/frontend.layout";
import { Grid, Row, Column } from "@ryerson/frontend.layout";
import { Media } from "@ryerson/frontend.next";
import { Breadcrumb, Link } from "@ryerson/frontend.navigation";
import { Typography } from "@ryerson/frontend.typography";
import { Video } from "@ryerson/frontend.media";
import { VideoWrapperProps } from "@components/Helper/common";
import { getVideoPadding } from "@components/Helper/commonStyles";
import { CareersButtons } from "@components/Shared/CareersButtons/CareersButtons";
import { Button } from "@ryerson/frontend.button";
import Spacer from "@components/Shared/Spacer/Spacer";

/** One language's hero copy. */
export type GrowHeroContent = {
	title: string;
	subTitle: string;
	/**
	 * Body text. On desktop a `\n` starts a new line (`white-space: pre-line`); on mobile it
	 * doesn't.
	 */
	description: string;
	/** YouTube video id. Absent or empty: `thumbnailUrl` is shown as a plain image instead. */
	videoId?: string;
	/** The video's poster image, or the hero image when there is no video. */
	thumbnailUrl: string;
	/**
	 * Accepted but has no effect: the desktop play button is placed `"right"` whatever the value,
	 * and the mobile one `"top"`.
	 */
	buttonOrientation?: string;
	/** Label of the optional button, shown only when `isCareers` is off and `buttonUrl` is set. */
	buttonLabel?: string;
	/**
	 * Target of the optional button, shown only when `isCareers` is off and `buttonLabel` is set.
	 */
	buttonUrl?: string;
	/** Label shown on the video poster (`Video` → `text`); unused when there is no video. */
	videoOverlayText?: string;
};

/** Props of `GrowHero`. */
export type GrowHeroProps = {
	content: GrowHeroContent;
	/** Shows the job-board buttons in place of the `buttonUrl` button. Default `false`. */
	isCareers?: boolean;
};

const BreadcrumbContainer = styled.div`
	margin-top: 32px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				margin-top: 8px;
			}
		`;
	}}
`;

const TextWrapper = styled.div`
	max-width: 359px;
`;

const VideoWrapper = styled.div`
	img {
		width: 100%;
		height: auto;
	}
	${(props: VideoWrapperProps) => {
		const { theme, buttonOrientation = "right", buttonSize = "md" } = props;
		return css`
			@media only screen and (min-width: ${theme.breakpoints.lg}) {
				max-width: 560px;
				height: 360px;
			}
			@media only screen and (max-width: ${theme.breakpoints.xl}) {
				max-width: ${buttonOrientation && buttonOrientation === "right"
					? "calc(100% - 40px)"
					: "560px"};
			}
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				min-height: 230px;
				margin-bottom: 30px;
				${buttonOrientation === "center"
					? 0
					: getVideoPadding(buttonOrientation, true, buttonSize)};
			}
		`;
	}}
`;

/** The hero, in a desktop and a mobile layout switched by `Media` at `lg`. */
const GrowHero: React.FC<GrowHeroProps> = ({ content, isCareers = false }) => {
	const { theme } = useTheme();

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<ContentSection type="tertiary">
					<BreadcrumbContainer theme={theme}>
						<Breadcrumb type="tertiary" size="xs" />
					</BreadcrumbContainer>
					<Typography
						variant="h1"
						type="tertiary"
						css={{ marginTop: "35px", marginBottom: "20px" }}
					>
						{content.title}
					</Typography>
					<Grid>
						<Row>
							<Column lg={6}>
								<TextWrapper>
									<Typography
										variant="h4"
										type="tertiary"
										css={{ marginTop: "12px", marginBottom: "23px" }}
									>
										{content.subTitle}
									</Typography>
									<Typography
										variant="p"
										type="tertiary"
										size="md"
										css={css`
											white-space: pre-line;
										`}
									>
										{content.description}
									</Typography>
								</TextWrapper>
							</Column>
							<Column lg={6}>
								<VideoWrapper
									theme={theme}
									buttonOrientation={
										content.buttonOrientation &&
										content.buttonOrientation === "right"
											? content.buttonOrientation
											: "right"
									}
								>
									{content.videoId && content.videoId.length > 0 ? (
										<Video
											videoId={content.videoId}
											imageUrl={content.thumbnailUrl}
											buttonOrientation={
												content.buttonOrientation &&
												content.buttonOrientation === "right"
													? content.buttonOrientation
													: "right"
											}
											text={content.videoOverlayText}
										/>
									) : (
										<img src={content.thumbnailUrl} alt={content.title} />
									)}
								</VideoWrapper>
							</Column>
						</Row>
					</Grid>
					{isCareers && <CareersButtons />}
					{!isCareers && content.buttonUrl && content.buttonLabel && (
						<Link to={content.buttonUrl}>
							<Button
								label={content.buttonLabel}
								type="secondary"
								shape="rounded"
								paddingType="relaxed"
								onClick={() => {}}
								size="sm"
							/>
						</Link>
					)}
					<Spacer
						css={css`
							height: 50px;
							background-color: ${theme.colors.primary.secondaryBrand};
						`}
					/>
				</ContentSection>
			</Media>
			<Media lessThan="lg">
				<ContentSection type="tertiary">
					<BreadcrumbContainer theme={theme}>
						<Breadcrumb type="tertiary" size="xs" />
					</BreadcrumbContainer>
					<Typography
						variant="h1"
						type="tertiary"
						css={{ marginTop: "10px", marginBottom: "24px" }}
					>
						{content.title}
					</Typography>
					<Typography variant="h4" type="tertiary" css={{ marginBottom: "20px" }}>
						{content.subTitle}
					</Typography>
				</ContentSection>
				<ContentSection type="secondary">
					<VideoWrapper theme={theme} buttonOrientation="top">
						{content.videoId && content.videoId.length > 0 ? (
							<Video
								videoId={content.videoId}
								imageUrl={content.thumbnailUrl}
								buttonOrientation="top"
								text={content.videoOverlayText}
							/>
						) : (
							<img src={content.thumbnailUrl} alt={content.title} />
						)}
					</VideoWrapper>
					<Typography variant="p" type="secondary" size="md">
						{content.description}
					</Typography>
					<Spacer
						css={css`
							height: 20px;
						`}
					/>
					{isCareers && <CareersButtons />}
					{!isCareers && content.buttonUrl && content.buttonLabel && (
						<Link to={content.buttonUrl}>
							<Button
								label={content.buttonLabel}
								type="secondary"
								shape="rounded"
								paddingType="relaxed"
								onClick={() => {}}
								size="sm"
							/>
						</Link>
					)}
					<Spacer
						css={css`
							height: 20px;
						`}
					/>
				</ContentSection>
			</Media>
		</>
	);
};

export default GrowHero;
